import React from "react";

/**
 * KSLogo
 * Stylized cyber "KS" monogram.
 *   - Geometric, ribbon-like "K" in white
 *   - Interlocking neon "S" in the design color (#ff014f)
 *   - Optional glow + orbiting accent dots
 *
 * Props:
 *   size       — number (px) for square width/height. Default 56.
 *   glow       — boolean. Add neon glow + outer ring. Default false.
 *   monochrome — boolean. Render in single (white) color. Default false.
 *   className  — extra classes on the wrapping <svg>.
 */
const KSLogo = ({
  size = 56,
  glow = false,
  monochrome = false,
  className = "",
}) => {
  const accent = monochrome ? "#ffffff" : "#ff014f";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Kushal Sharma logo"
      role="img"
    >
      <defs>
        <linearGradient id="ksAccent" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ff3672" />
          <stop offset="55%" stopColor={accent} />
          <stop offset="100%" stopColor="#b8003a" />
        </linearGradient>
        <linearGradient id="ksLight" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#c8d2e4" />
        </linearGradient>
        <filter id="ksGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g filter={glow ? "url(#ksGlow)" : undefined}>
        {/* Soft outer ring (only with glow flag) */}
        {glow && (
          <>
            <circle
              cx="60"
              cy="60"
              r="56"
              fill="none"
              stroke="url(#ksAccent)"
              strokeWidth="1"
              strokeDasharray="2 6"
              opacity="0.6"
            />
            <circle
              cx="60"
              cy="60"
              r="50"
              fill="none"
              stroke="#ffffff"
              strokeWidth="0.5"
              strokeDasharray="1 4"
              opacity="0.25"
            />
          </>
        )}

        {/* "K" — clean geometric form */}
        <path
          d="M 22 14
             H 36
             V 52
             L 66 14
             H 84
             L 52 56
             L 84 106
             H 66
             L 42 68
             L 36 75
             V 106
             H 22
             Z"
          fill="url(#ksLight)"
        />

        {/* "S" — flowing accent, interlocks with K's upper arm */}
        <path
          d="M 106 32
             C 106 21, 96 14, 84 14
             C 70 14, 60 22, 60 36
             C 60 48, 70 53, 84 57
             C 95 60, 100 63, 100 69
             C 100 77, 92 81, 82 81
             C 72 81, 64 76, 60 68
             L 52 76
             C 58 92, 70 102, 84 102
             C 102 102, 114 92, 114 72
             C 114 59, 104 53, 88 49
             C 76 46, 72 43, 72 37
             C 72 31, 78 27, 86 27
             C 94 27, 100 31, 102 37 Z"
          fill="url(#ksAccent)"
        />

        {/* Accent corner nodes */}
        <circle cx="108" cy="100" r="3.2" fill={accent} />
        <circle cx="20" cy="18" r="2" fill="#ffffff" opacity="0.7" />
      </g>
    </svg>
  );
};

export default KSLogo;
